import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  NavLink,
  Link,
  Outlet,
} from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Diagram2, PeopleFill, ShieldLock, Cloud } from "react-bootstrap-icons";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdminPage from "./pages/AdminPage";
import SsoSettingsPage from "./pages/SsoSettingsPage";
import AuthCallback from "./pages/AuthCallback";
import AzDoSettingsPage from "./pages/AzDoSettingsPage";
import { DialogsProvider } from "./components/ui/dialogs";
import { RealtimeProvider } from "./realtime/RealtimeProvider";
import { cn } from "./lib/cn";
import {
  installSessionInterceptor,
  onSessionRenewed,
  onSessionExpired,
} from "./lib/session";

interface DecodedToken {
  username: string;
  iat: number;
  /** Seconds since the epoch. Absent on tokens issued before expiry existed. */
  exp?: number;
}

const API_URL = import.meta.env.VITE_API_URL || "";

const AdminRoute = ({
  role,
  children,
}: {
  role: string;
  children: React.ReactNode;
}) => {
  // Both admins and approvers may enter the admin layout; the individual
  // settings links below are still admin-only.
  if (role !== "admin" && role !== "approver") {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const adminNavClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium",
    "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
    isActive
      ? "bg-accent-soft text-accent"
      : "text-ink-muted hover:bg-surface-hover hover:text-ink",
  );

const AdminLayout = ({ role }: { role: string }) => (
  <div className="bg-canvas flex h-full overflow-hidden">
    <nav
      aria-label="Admin sections"
      className="bg-surface border-line flex w-60 shrink-0 flex-col gap-1 overflow-y-auto border-e p-3"
    >
      <p className="text-ink-subtle px-3 pt-1 pb-2 text-[11px] font-semibold tracking-wider uppercase">
        Administration
      </p>

      <NavLink to="/admin" end className={adminNavClass}>
        <PeopleFill size={14} />
        Users &amp; groups
      </NavLink>

      {role === "admin" && (
        <>
          <NavLink to="/admin/settings" className={adminNavClass}>
            <ShieldLock size={14} />
            SSO settings
          </NavLink>
          <NavLink to="/admin/azdo-settings" className={adminNavClass}>
            <Cloud size={14} />
            Azure DevOps sync
          </NavLink>
        </>
      )}
    </nav>

    <div className="min-w-0 flex-1 overflow-y-auto">
      <Outlet />
    </div>
  </div>
);

function App() {
  // Initialised synchronously from localStorage: reading the token in an effect
  // caused a split-second redirect that wiped the URL's query parameters, which
  // broke deep links into a specific diagram.
  const getInitialAuth = () => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        const decoded = jwtDecode<DecodedToken>(storedToken);
        // Sessions expire now. Discarding a stale token here avoids a round of
        // 401s on load and sends the user straight to the sign-in screen.
        if (decoded.exp && decoded.exp * 1000 <= Date.now()) {
          localStorage.removeItem("token");
        } else {
          // The role is deliberately NOT read from the token any more: it is
          // the server's answer to /api/me, so a demotion takes effect on the
          // next load instead of lingering until the token is replaced.
          return { token: storedToken, username: decoded.username, role: "" };
        }
      } catch {
        localStorage.removeItem("token");
      }
    }
    return { token: "", username: "", role: "" };
  };

  const [authState, setAuthState] = useState(getInitialAuth);
  const { token, username, role } = authState;

  // Set when a session lapses mid-use, so the sign-in screen can say why
  // rather than appearing for no visible reason.
  const [sessionLapsed, setSessionLapsed] = useState(false);

  // Resolves the role for a token restored from storage, and catches a session
  // that expired or an account that was removed while the tab was open.
  useEffect(() => {
    if (!token || role) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          localStorage.removeItem("token");
          if (!cancelled) setAuthState({ token: "", username: "", role: "" });
          return;
        }
        if (!res.ok) return;
        const me = await res.json();
        if (!cancelled) {
          setAuthState((prev) => ({ ...prev, username: me.username, role: me.role }));
        }
      } catch {
        // Offline: leave the session alone rather than signing someone out
        // because their network blipped.
      }
    })();

    return () => { cancelled = true; };
  }, [token, role]);

  // Session continuity. The interceptor is installed once and reports two
  // things: a token the server rotated, and a session that has lapsed.
  useEffect(() => {
    installSessionInterceptor();

    const offRenewed = onSessionRenewed((fresh) => {
      setAuthState((prev) => (prev.token === fresh ? prev : { ...prev, token: fresh }));
    });

    const offExpired = onSessionExpired(() => {
      setAuthState({ token: "", username: "", role: "" });
      setSessionLapsed(true);
    });

    return () => { offRenewed(); offExpired(); };
  }, []);

  // Dropping a file anywhere the app does not handle makes the browser navigate
  // to it, silently discarding unsaved work. Swallow those. Handlers that do
  // accept a drop call preventDefault first, so this never overrides them.
  useEffect(() => {
    const swallow = (event: DragEvent) => {
      if (!Array.from(event.dataTransfer?.types ?? []).includes("Files")) return;
      event.preventDefault();
      if (event.type === "drop" && event.dataTransfer) {
        event.dataTransfer.dropEffect = "none";
      }
    };
    window.addEventListener("dragover", swallow);
    window.addEventListener("drop", swallow);
    return () => {
      window.removeEventListener("dragover", swallow);
      window.removeEventListener("drop", swallow);
    };
  }, []);

  const handleLoginSuccess = (
    newToken: string,
    newUsername: string,
    newRole: string,
  ) => {
    localStorage.setItem("token", newToken);
    setAuthState({ token: newToken, username: newUsername, role: newRole });
    setSessionLapsed(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setAuthState({ token: "", username: "", role: "" });
    // Signing out deliberately is not a lapsed session; saying so would be
    // both wrong and alarming.
    setSessionLapsed(false);
  };

  const isPrivileged = role === "admin" || role === "approver";

  return (
    <BrowserRouter>
      <DialogsProvider>
        {/* Keyed on the token so a re-login tears down the old socket and
            authenticates a fresh one rather than reusing a stale identity. */}
        <RealtimeProvider key={token || "anonymous"} token={token}>
        <div className="flex h-full flex-col overflow-hidden">
          {token && (
            <header className="bg-surface border-line flex h-12 shrink-0 items-center justify-between border-b px-3">
              <Link
                to="/"
                className="text-ink hover:text-accent flex items-center gap-2 text-sm font-semibold tracking-tight transition-colors duration-(--duration-instant)"
              >
                <span className="bg-accent flex h-6 w-6 items-center justify-center rounded-md text-white">
                  <Diagram2 size={14} />
                </span>
                SCHEmunda
              </Link>

              {isPrivileged && (
                <nav className="flex items-center gap-1">
                  <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                      cn(
                        "rounded-lg px-3 py-1.5 text-[13px] font-medium",
                        "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
                        isActive
                          ? "bg-surface-sunken text-ink"
                          : "text-ink-muted hover:bg-surface-hover hover:text-ink",
                      )
                    }
                  >
                    Editor
                  </NavLink>
                  <NavLink
                    to="/admin"
                    className={({ isActive }) =>
                      cn(
                        "rounded-lg px-3 py-1.5 text-[13px] font-medium",
                        "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
                        isActive
                          ? "bg-surface-sunken text-ink"
                          : "text-ink-muted hover:bg-surface-hover hover:text-ink",
                      )
                    }
                  >
                    Admin
                  </NavLink>
                </nav>
              )}
            </header>
          )}

          <div className="min-h-0 flex-1 overflow-hidden">
            <Routes>
              <Route
                path="/login"
                // Search params are preserved so a deep link survives login.
                element={
                  !token ? (
                    <Login
                      onLoginSuccess={handleLoginSuccess}
                      sessionLapsed={sessionLapsed}
                    />
                  ) : (
                    <Navigate to={`/${window.location.search}`} />
                  )
                }
              />

              <Route
                path="/auth/callback"
                element={<AuthCallback onLoginSuccess={handleLoginSuccess} />}
              />

              <Route
                path="/"
                element={
                  token ? (
                    <Dashboard
                      token={token}
                      username={username}
                      role={role}
                      onLogout={handleLogout}
                    />
                  ) : (
                    <Navigate to={`/login${window.location.search}`} />
                  )
                }
              />

              <Route
                path="/admin"
                element={
                  token ? (
                    <AdminRoute role={role}>
                      <AdminLayout role={role} />
                    </AdminRoute>
                  ) : (
                    <Navigate to={`/login${window.location.search}`} />
                  )
                }
              >
                <Route index element={<AdminPage token={token} role={role} />} />
                <Route path="settings" element={<SsoSettingsPage token={token} />} />
                <Route
                  path="azdo-settings"
                  element={<AzDoSettingsPage token={token} />}
                />
              </Route>

              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </div>
        </div>
        </RealtimeProvider>
      </DialogsProvider>
    </BrowserRouter>
  );
}

export default App;
